import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Train, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Even on transport errors, surface the generic success message
      // so we never leak whether an account exists.
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen flex items-center justify-center px-6 pt-safe-navbar">
      <div className="iq-card w-full max-w-md p-8 animate-fade-up">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-gold text-ink rounded-sm">
            <Train className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl tracking-[0.18em] text-cream">INTERQUEST</span>
        </Link>
        <h1 className="font-display text-cream text-4xl tracking-wider mb-1">Forgot password</h1>
        <p className="font-serif italic text-cream-dim mb-6">We'll send a reset link to your inbox.</p>

        {sent ? (
          <div data-testid="forgot-password-success" className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-gold shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-cream text-2xl tracking-wider mb-1">Check your inbox</h2>
                <p className="font-serif italic text-cream-dim leading-relaxed">
                  If that address is registered, a reset link is on its way. It may take a minute to arrive.
                </p>
              </div>
            </div>
            <Link to="/login" data-testid="forgot-password-back-login" className="iq-btn-secondary w-full justify-center">
              Back to sign in <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="iq-label">Email</label>
              <input
                data-testid="forgot-password-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="iq-input"
              />
            </div>
            {error && (
              <div data-testid="forgot-password-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-3 py-2 rounded-sm text-sm">
                {error}
              </div>
            )}
            <button data-testid="forgot-password-submit" type="submit" disabled={loading} className="iq-btn-primary w-full">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send reset link <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-cream-dim mt-6">
          Remembered it?{" "}
          <Link to="/login" data-testid="forgot-password-link-login" className="text-gold hover:text-cream">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
